import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { useTeamData } from '../../../app/use-team-data'
import { PASSAGE_NOTICES_QUERY_KEY } from '../../../offline/query-keys'

/**
 * L'instant du **dernier avis de passage** laissé depuis cette tablette, par demande — ce que
 * `tourStateOf` lit pour dire « Avis de passage » (Terrain 2.8).
 *
 * ⚠️ Une seule lecture pour la liste, l'agenda et l'en-tête de la fiche : lus séparément, ils
 * diraient trois états différents du même contrôle.
 */
export function usePassageNoticedAt(): ReadonlyMap<string, string> {
  const data = useTeamData()
  const notices = useQuery({
    queryKey: PASSAGE_NOTICES_QUERY_KEY,
    queryFn: () => data.passageNotices.toArray(),
  })

  return useMemo(() => {
    const latest = new Map<string, string>()
    for (const notice of notices.data ?? []) {
      const known = latest.get(notice.inquiryId)
      if (known === undefined || notice.noticedAt > known) {
        latest.set(notice.inquiryId, notice.noticedAt)
      }
    }

    return latest
  }, [notices.data])
}

/**
 * Les relances déposées **encore en file** sur la tablette, par demande (M40) — celles que le
 * serveur n'a pas encore comptées dans `remindersLeft`.
 *
 * ponytail: entre l'envoi et la descente suivante de la tournée, une relance partie n'est comptée
 * nulle part ; le numéro proposé peut redescendre d'un cran pendant ce temps.
 */
export function usePendingReminders(): ReadonlyMap<string, number> {
  const data = useTeamData()
  const notices = useQuery({
    queryKey: PASSAGE_NOTICES_QUERY_KEY,
    queryFn: () => data.passageNotices.toArray(),
  })

  return useMemo(() => {
    const pending = new Map<string, number>()
    for (const notice of notices.data ?? []) {
      if (notice.reason === 'reminder_left' && notice.state !== 'sent') {
        pending.set(notice.inquiryId, (pending.get(notice.inquiryId) ?? 0) + 1)
      }
    }

    return pending
  }, [notices.data])
}
